import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { AuthLayout } from '../Auth/AuthLayout';
import { publicRegistrationService } from '../../services/publicRegistrationService';
import { sanitizeEmail } from '../../utils/inputSanitizer';

const STEPS = ['Account', 'Verify email'];
const MIN_PASSWORD_LENGTH = 8;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FieldErrors = Partial<Record<'email' | 'password' | 'confirmPassword' | 'code', string>>;

export const SignUp: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  /** Errors are attached to their field so the fix is where the user is looking. */
  const validate = (): boolean => {
    const errors: FieldErrors = {};
    if (!email) errors.email = 'Enter your email address.';
    else if (!EMAIL_PATTERN.test(email)) errors.email = 'That does not look like a valid email address.';

    if (!password) errors.password = 'Choose a password.';
    else if (password.length < MIN_PASSWORD_LENGTH)
      errors.password = `Use at least ${MIN_PASSWORD_LENGTH} characters.`;

    if (!confirmPassword) errors.confirmPassword = 'Repeat your password.';
    else if (password !== confirmPassword) errors.confirmPassword = 'The two passwords do not match.';

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSignUp = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await publicRegistrationService.signUp(email, password);
      if (response.result) {
        setSuccess(response.message);
        setActiveStep(1);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign up failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (confirmationCode.length !== 6) {
      setFieldErrors({ code: 'The code is six digits.' });
      return;
    }
    setFieldErrors({});

    setLoading(true);
    try {
      const response = await publicRegistrationService.confirmSignUp(email, confirmationCode, password);
      if (response.result) {
        setSuccess(`${response.message} Taking you to sign in…`);
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Confirmation failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const response = await publicRegistrationService.resendConfirmationCode(email);
      if (response.result) setSuccess(response.message);
      else setError(response.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend the code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Register, verify your email, then ask an administrator to grant console access."
      footer={
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Already registered?{' '}
          <Link component={RouterLink} to="/login">
            Sign in
          </Link>
        </Typography>
      }
    >
      <Stepper activeStep={activeStep} sx={{ mb: 3.5 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" role="alert" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2.5 }}>
          {success}
        </Alert>
      )}

      {activeStep === 0 && (
        <Box component="form" onSubmit={handleSignUp} noValidate>
          <Stack spacing={2.25}>
            <TextField
              fullWidth
              size="medium"
              label="Email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(sanitizeEmail(event.target.value))}
              error={Boolean(fieldErrors.email)}
              helperText={fieldErrors.email}
              required
              disabled={loading}
            />

            <TextField
              fullWidth
              size="medium"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              error={Boolean(fieldErrors.password)}
              helperText={fieldErrors.password ?? `At least ${MIN_PASSWORD_LENGTH} characters.`}
              required
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <VisibilityOffOutlinedIcon fontSize="small" />
                      ) : (
                        <VisibilityOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              size="medium"
              label="Confirm password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              error={Boolean(fieldErrors.confirmPassword)}
              helperText={fieldErrors.confirmPassword}
              required
              disabled={loading}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ mt: 0.5 }}
            >
              {loading ? 'Creating account…' : 'Create account'}
            </Button>
          </Stack>
        </Box>
      )}

      {activeStep === 1 && (
        <Box component="form" onSubmit={handleConfirmSignUp} noValidate>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
            We sent a six-digit code to <strong>{email}</strong>. Enter it below to verify the address.
          </Typography>

          <Stack spacing={2.25}>
            <TextField
              fullWidth
              size="medium"
              label="Confirmation code"
              value={confirmationCode}
              onChange={(event) => setConfirmationCode(event.target.value.replace(/\D/g, ''))}
              error={Boolean(fieldErrors.code)}
              helperText={fieldErrors.code ?? 'Check your inbox, and your spam folder.'}
              required
              disabled={loading}
              inputProps={{ maxLength: 6, inputMode: 'numeric', className: 'mono' }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {loading ? 'Verifying…' : 'Verify email'}
            </Button>

            <Button fullWidth variant="text" onClick={handleResendCode} disabled={loading}>
              Resend code
            </Button>
          </Stack>
        </Box>
      )}
    </AuthLayout>
  );
};

export default SignUp;
